import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, IconButton, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getEmptyMenuItem, MenuItem } from '../../interfaces/Menu/MenuItem';
import { getMenuItems, updateMenuItemOrders } from '../../api/MenuItem';
import { findMenuItemInTree } from '../../utils/Menu';
import { sharedStyles } from '../../utils/Style';
import { Waiting } from '../../components/Waiting/Waiting';
import { MenuItemTree } from './MenuItemTree/MenuItemTree';
import { ModifyMenuItemSection } from './MenuItemTree/ModifyMenuItemSection';
import { MenuItemOrder } from '../../interfaces/Menu/MenuItemOrder';

export const MenuItemsPage = () => {
  const navigate = useNavigate();

  const { menuID: menuIDAsStr } = useParams<string>();

  const menuID = menuIDAsStr ? Number(menuIDAsStr) : undefined;

  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<MenuItem | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(false);

  const validMenu = Number.isFinite(menuID);

  const refreshMenuItems = (selectedMenuItemID?: number) => {
    if (!validMenu) {
      setMenuItems([]);
      return;
    }

    setLoading(true);
    getMenuItems(menuID!)
      .then((items) => {
        setMenuItems(items);
        if (Number.isFinite(selectedMenuItemID)) {
          setSelectedItem(findMenuItemInTree(items, selectedMenuItemID!));
        }
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshMenuItems();
  }, [menuID]);

  const handleAddRoot = () => {
    setSelectedItem(getEmptyMenuItem(menuID ?? 0, menuItems.length));
  };

  const handleAddChild = (parentID: number, order: number) => {
    setSelectedItem({ ...getEmptyMenuItem(menuID ?? 0, order), parentID });
  };

  const handleDelete = () => {
    setSelectedItem(undefined);
    refreshMenuItems();
  };

  const handleChangeOrder = (
    index: number,
    direction: 'up' | 'down',
    parentID?: number,
  ) => {
    const items = Number.isFinite(parentID)
      ? findMenuItemInTree(menuItems, parentID!)?.children
      : menuItems;
    if (!items) return;

    const updates: MenuItemOrder[] = [
      {
        id: items[index].id,
        order: items[direction === 'up' ? index - 1 : index + 1].itemOrder,
      },
      {
        id: items[direction === 'up' ? index - 1 : index + 1].id,
        order: items[index].itemOrder,
      },
    ];

    setLoading(true);
    updateMenuItemOrders(updates)
      .then(() => {
        refreshMenuItems(items[index].id);
      })
      .catch((err) => {
        toast.error(err.message);
        setLoading(false);
      });
  };

  const handleSelect = (id: number) => {
    setSelectedItem(findMenuItemInTree(menuItems, id));
  };

  const goBack = () => navigate('/visual/menus');

  return (
    <Box sx={sharedStyles.pageBody}>
      <Waiting open={loading} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 3,
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h5'>
          {validMenu ? 'Ögeler' : 'Menü Bulunamadı'}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        {validMenu && (
          <MenuItemTree
            key={menuItems.length}
            selectedItem={selectedItem}
            menuItems={menuItems}
            onAddRoot={handleAddRoot}
            onAddChild={handleAddChild}
            onDelete={handleDelete}
            onSelect={handleSelect}
            onChangeOrder={handleChangeOrder}
          />
        )}
        {!!selectedItem && (
          <ModifyMenuItemSection
            menuItem={selectedItem}
            menuItems={menuItems}
            onModify={refreshMenuItems}
            onCancel={() => setSelectedItem(undefined)}
          />
        )}
      </Box>
    </Box>
  );
};
